import { supabase } from '../lib/supabase';

/**
 * Stripe Service - Secure Client-Side Operations
 * 
 * ⚠️ SECURITY: This service only uses VITE_STRIPE_PUBLISHABLE_KEY (safe for browser).
 * All operations requiring STRIPE_SECRET_KEY are handled by Edge Functions.
 */

/**
 * Create Stripe Setup Intent for card tokenization
 * Uses Edge Function to keep secret key secure
 */
export async function createSetupIntent(userId) {
  try {
    // Call Edge Function to create Setup Intent securely
    const { data, error } = await supabase?.functions?.invoke('create-setup-intent', {
      body: { userId }
    });

    if (error) throw error;

    return { success: true, clientSecret: data?.clientSecret };
  } catch (error) {
    console.error('Create setup intent error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Save payment method to user profile
 */
export async function savePaymentMethod(userId, paymentMethodId, cardDetails) {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.update({
        card_on_file_at: new Date()?.toISOString(),
        stripe_payment_method_id: paymentMethodId,
        card_last4: cardDetails?.last4,
        card_brand: cardDetails?.brand
      })
      ?.eq('id', userId)
      ?.select()
      ?.single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Save payment method error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Check if user has card on file
 */
export async function hasCardOnFile(userId) {
  try {
    // Check test account status from user_profiles
    const { data: profile, error: profileError } = await supabase
      ?.from('user_profiles')
      ?.select('is_test_account, test_account_status')
      ?.eq('id', userId)
      ?.single();

    if (profileError) throw profileError;

    // Test accounts or pending test account requests bypass card requirement
    if (profile?.is_test_account || profile?.test_account_status === 'pending' || profile?.test_account_status === 'approved') {
      return {
        success: true,
        hasCard: true,
        isTestAccount: true,
        cardDetails: {
          last4: 'TEST',
          brand: 'Test Account'
        }
      };
    }

    // Check user_payment_methods table — synced from Stripe by sync-payment-methods
    const { data: methods, error: methodsError } = await supabase
      ?.from('user_payment_methods')
      ?.select('card_brand, card_last4')
      ?.eq('user_id', userId)
      ?.limit(1);

    if (methodsError) throw methodsError;

    const hasCard = Array.isArray(methods) && methods.length > 0;

    return {
      success: true,
      hasCard,
      cardDetails: hasCard ? {
        last4: methods[0]?.card_last4,
        brand: methods[0]?.card_brand
      } : null
    };
  } catch (error) {
    console.error('Check card on file error:', error);
    return { success: false, error: error?.message };
  }
}